<template>
  <BaseDialog
    :title="$t('common.requestFeedback')"
    :is-open="isOpen"
    type="responsive"
    class="lg:min-w-160"
    :align-dismiss-button-right="true"
    @close="emit('close-dialog')"
  >
    <LoadingWrapper :state="loadingStatus">
      <div v-if="step == 'select-templates'">
        <FeedbackTopicList
          v-if="templates"
          v-model="form.templateIds"
          :bold="true"
          :templates="templates"
          :show-perspectives="false"
          :require-shared-perspectives="true"
          :error="errors.get('templateIds')"
        />
      </div>
      <div v-if="step == 'select-question-groups'">
        <QuestionGroupSectionSelect
          v-model="form.questionGroupIds"
          :templates="configurableTemplates"
          :error="errors.get('questionGroupIds')"
        />
      </div>
      <div v-if="step == 'select-perspectives'" class="flex flex-col gap-0.5">
        <h3
          class="text-title-small mb-2"
          :class="[
            !errors.get('perspectives') && 'text-on-surface-variant',
            errors.get('perspectives') && 'text-error',
          ]"
        >
          {{ $t('startSurvey.selectPerspectives') }}
        </h3>
        <div class="flex flex-col">
          <div>
            <div class="flex flex-col gap-3">
              <PerspectiveGatheringItem
                v-for="perspective in availablePerspectives"
                :key="perspective.key"
                :perspective="perspective"
                :state="ensurePerspectiveState(perspective.key)"
                :required="requiredPerspectiveKeys.has(perspective.key)"
                :error="errors.get(perspective.key)"
                :alreadySelectedMemberIds="selectedMemberIds"
                member-capability="canGiveFeedback"
              />
            </div>
            <p v-if="errors.get('perspectives')" class="text-error text-body-medium my-2 px-4">
              {{ errors.get('perspectives') }}
            </p>
          </div>
          <hr class="border-outline-variant -mx-6 my-4" />
          <BaseSwitchListItem
            v-if="includeSelfAvailable"
            v-model="form.includeSelf"
            v-tooltip="selfRequired ? $t('startSurvey.perspectiveRequiredTooltip') : undefined"
            :title="$t('startSurvey.addSelfFeedback')"
            :supporting-text="$t('startSurvey.addSelfFeedbackDescription')"
            icon="account_circle"
            :disabled="selfRequired"
            class="rounded-large bg-surface-container-low"
          />
        </div>
      </div>
      <div v-if="step == 'add-metadata'">
        <BaseInputField
          v-model="form.title"
          type="text"
          :label="$t('common.title')"
          :error="errors.get('title')"
          class="bg-surface-container-high"
        />
        <DialogBreak />
        <h3 class="text-title-medium">{{ $t('common.message') }} ({{ $t('common.optional') }})</h3>
        <p class="text-body-medium text-on-surface-variant">
          {{ $t('feedback.messageDescription') }}
        </p>
        <WysiwygTextField v-model="form.message" />
      </div>
    </LoadingWrapper>

    <template #confirm>
      <div class="flex flex-row gap-1">
        <BaseTextButton v-if="!isFirstStep" size="M" color="outlined" @click="previousStep">{{
          $t('common.back')
        }}</BaseTextButton>
        <BaseTextButton v-if="!isLastStep" size="M" color="filled" @click="nextStep">{{
          $t('common.continue')
        }}</BaseTextButton>
        <BaseTextButton v-if="isLastStep" size="M" color="filled" @click="saveSurvey">{{
          $t('common.request')
        }}</BaseTextButton>
      </div>
    </template>
    <template #dismiss>
      <BaseTextButton size="M" color="textOnSurface" @click="emit('close-dialog')">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import DialogBreak from '@/core/components/base/dialog/DialogBreak.vue'
  import WysiwygTextField from '@/core/components/base/wysiwyg/WysiwygTextField.vue'
  import { watch } from 'vue'
  import { requestFeedback } from './use-request-feedback'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useRoute } from 'vue-router'
  import BaseSwitchListItem from '@/core/components/base/switch-list-item/BaseSwitchListItem.vue'
  import FeedbackTopicList from '@/core/components/feedback-topic-list/FeedbackTopicList.vue'
  import QuestionGroupSectionSelect from '@/core/components/question-group-section-select/QuestionGroupSectionSelect.vue'
  import PerspectiveGatheringItem from '@/core/components/perspective-gathering/PerspectiveGatheringItem.vue'

  interface Props {
    isOpen: boolean
  }

  const props = defineProps<Props>()

  const {
    previousStep,
    nextStep,
    step,
    isFirstStep,
    isLastStep,
    saveSurvey,
    form,
    errors,
    templates,
    clearForm,
    loadingStatus,
    availablePerspectives,
    includeSelfAvailable,
    selfRequired,
    requiredPerspectiveKeys,
    configurableTemplates,
    autoSelectSingleTemplate,
    ensurePerspectiveState,
    selectedMemberIds,
  } = requestFeedback(() => emit('close-dialog'))

  const emit = defineEmits<{
    (e: 'close-dialog'): void
  }>()

  const route = useRoute()

  watch(
    () => props.isOpen,
    (isOpen) => {
      if (!isOpen) {
        clearForm()
      }
      if (isOpen) {
        if (route.query.templateId) {
          form.templateIds.push(route.query.templateId as string)
        } else {
          autoSelectSingleTemplate()
        }
      }
    },
    { immediate: true },
  )
</script>
